import { makeAutoObservable, runInAction } from 'mobx'

import { userManager } from '@/Core/Managers'
import { newPasswordError } from '@/Ship/Helpers/passwordPolicy'
import { serverMessage } from '@/Ship/Helpers/serverError'

import { PROFILE_FIELDS, PROFILE_TEXTS, SESSION_USER_FIELDS } from '../Contracts/IProfile'
import { UpdateCurrentUserTask } from '../Tasks/UpdateCurrentUserTask'
import { UploadUserPhotoTask } from '../Tasks/UploadUserPhotoTask'

/**
 * Стан «Мого профілю»: збереження даних, фото й пароля.
 *
 * Відповідь бека одразу йде в користувача сесії (`userManager.user`) — на
 * нього підписаний аватар у шапці, тож нове фото й ім'я видно без
 * перезавантаження (С2.1). У легасі аватар зникав до наступного `/me` (D-29).
 */
export class ProfileManager {
    isInfoSaving = false
    isPhotoBusy = false
    isPasswordSaving = false

    infoError: string | null = null
    photoError: string | null = null
    passwordError: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    get uuid(): string | null {
        const uuid = userManager.user['uuid']

        return typeof uuid === 'string' && uuid !== '' ? uuid : null
    }

    /** Ім'я, прізвище, по батькові, пошта, посада. `true` — збережено. */
    async saveInfo(values: Record<string, unknown>): Promise<boolean> {
        const uuid = this.uuid

        if (!uuid) return false

        const json: Record<string, unknown> = {}

        for (const field of PROFILE_FIELDS) {
            const value = values[field]

            json[field] = typeof value === 'string' ? value.trim() : null
        }

        this.isInfoSaving = true
        this.infoError = null

        try {
            this.applyToSession(await UpdateCurrentUserTask(uuid, json))

            return true
        } catch (error) {
            runInAction(() => {
                this.infoError = serverMessage(error, PROFILE_TEXTS.unknown)
            })

            return false
        } finally {
            runInAction(() => {
                this.isInfoSaving = false
            })
        }
    }

    /** Нове фото: завантажити, одразу прикріпити до себе, показати в шапці. */
    async uploadPhoto(file: File): Promise<boolean> {
        const uuid = this.uuid

        if (!uuid) return false

        this.isPhotoBusy = true
        this.photoError = null

        try {
            const photo = await UploadUserPhotoTask(file)
            const reference = photo['@id'] ?? photo.uuid

            if (!reference) throw new Error(PROFILE_TEXTS.unknown)

            const user = await UpdateCurrentUserTask(uuid, { photo: reference })

            // Посилання на фото дає бек у відповіді PATCH: до прикріплення
            // підписане посилання завантаженого файлу ще не відкривається
            this.applyToSession({ ...user, photo: user['photo'] ?? photo })

            return true
        } catch (error) {
            runInAction(() => {
                this.photoError = serverMessage(error, PROFILE_TEXTS.unknown)
            })

            return false
        } finally {
            runInAction(() => {
                this.isPhotoBusy = false
            })
        }
    }

    /** «Видалити фото»: `PATCH {photo: null}` — окремого маршруту немає (`SPEC-005` §13). */
    async removePhoto(): Promise<boolean> {
        const uuid = this.uuid

        if (!uuid) return false

        this.isPhotoBusy = true
        this.photoError = null

        try {
            const user = await UpdateCurrentUserTask(uuid, { photo: null })

            this.applyToSession({ ...user, photo: null })

            return true
        } catch (error) {
            runInAction(() => {
                this.photoError = serverMessage(error, PROFILE_TEXTS.unknown)
            })

            return false
        } finally {
            runInAction(() => {
                this.isPhotoBusy = false
            })
        }
    }

    /** Фото відкинуто ще до запиту: не той формат чи завеликий файл. */
    rejectPhoto(text: string): void {
        this.photoError = text
    }

    /**
     * Новий пароль. Розбіжність і закороткий пароль ловимо до запиту — у
     * легасі підтвердження не перевірялося зовсім (D-28).
     */
    async changePassword(password: string, confirmation: string): Promise<boolean> {
        const invalid = newPasswordError(password, confirmation)

        if (invalid !== null) {
            this.passwordError = invalid
            return false
        }

        const uuid = this.uuid

        if (!uuid) return false

        this.isPasswordSaving = true
        this.passwordError = null

        try {
            await UpdateCurrentUserTask(uuid, { plainPassword: password })

            return true
        } catch (error) {
            runInAction(() => {
                this.passwordError = serverMessage(error, PROFILE_TEXTS.unknown)
            })

            return false
        } finally {
            runInAction(() => {
                this.isPasswordSaving = false
            })
        }
    }

    /** Переносить у сесію лише те, що показує оболонка, — ролей і токенів не чіпає. */
    private applyToSession(response: Record<string, unknown>): void {
        const patch: Record<string, unknown> = {}

        for (const field of SESSION_USER_FIELDS) {
            if (field in response) patch[field] = response[field]
        }

        runInAction(() => {
            userManager.user = { ...userManager.user, ...patch }
        })
    }
}
